import {useCallback, useState} from "react";
import {useKeycloakFunctions} from "./useKeycloakFunctions";
import {customerProductsAPI} from "../common/api/customerProductsAPI";
import {userAccountsAPI} from "../common/api/userAccountsAPI";
import {
    API_POINTS_PLATFORM,
    API_POINTS_PLATFORM_ACCESS,
    API_POINTS_PLATFORM_CANCEL,
    API_POINTS_PLATFORM_OFFER,
    API_POINTS_PLATFORM_OFFER_PURCHASE,
    API_POINTS_PLATFORM_OFFER_SEARCH,
    API_USER_TERMS_CONDITIONS
} from "../common/api/apiConstants";
import {createURLQueryFromParams} from "../common/api/apiUtil";

export const usePointsPlatform = () => {

    const {initialized, authenticated} = useKeycloakFunctions();

    const [deletedSaleOffer, setDeletedSaleOffer] = useState();
    const [loadingDeletedOffer, setLoadingDeletedOffer] = useState(false);
    const [errorDeletedOffer, setErrorDeletedOffer] = useState();

    const [loadingAccessAllowed, setLoadingAccessAllowed] = useState(false);
    const [errorAccessAllowed, setErrorAccessAllowed] = useState();
    const [pointsPlatformAccess, setPointsPlatformAccess] = useState();

    const [loadingAcceptTermsConditions, setLoadingAcceptTermsConditions] = useState(false);
    const [errorAcceptTermsConditions, setErrorAcceptTermsConditions] = useState(false);

    const [pointsOffer, setPointsOffer] = useState();
    const [loadingPointsOffer, setLoadingPointsOffer] = useState(false);
    const [errorPointsOffer, setErrorPointsOffer] = useState(null);

    const [resultOffer, setResultOffer] = useState();
    const [loadingSearchOffer, setLoadingSearchOffer] = useState(false);
    const [errorSearchOffer, setErrorSearchOffer] = useState(null);

    const [purchase, setPurchasePoints] = useState();
    const [loadingPointsPurchase, setLoadingPointsPurchase] = useState(false);
    const [errorPointsPurchase, setErrorPointsPurchase] = useState(null);

    const deleteSaleOffer = (saleOfferId) => {
        if (initialized && authenticated) {
            setLoadingDeletedOffer(true);
            setErrorDeletedOffer(null);

            return customerProductsAPI.delete(API_POINTS_PLATFORM_OFFER + "/" + saleOfferId + API_POINTS_PLATFORM_CANCEL)
                .then(res => {
                    setDeletedSaleOffer(res.data);
                    return res.data;
                })
                .catch(err => {
                    setErrorDeletedOffer(err.response);
                })
                .finally(() => {
                    setLoadingDeletedOffer(false);
                });
        } else {
            return new Promise(resolve => resolve());
        }
    };

    const accessAllowed = useCallback((access_type = null) => {
        setLoadingAccessAllowed(true);
        setErrorAccessAllowed(null);

        let url = API_POINTS_PLATFORM + API_POINTS_PLATFORM_ACCESS;
        if(access_type) {
            url += createURLQueryFromParams({access_type});
        }

        return customerProductsAPI.get(url)
            .then(res => {
                setPointsPlatformAccess(res.data);
                return res.data;
            })
            .catch(err => {
                setErrorAccessAllowed(err.response);
            })
            .finally(() => {
                setLoadingAccessAllowed(false);
            });
    }, [pointsPlatformAccess]);

    const acceptPointsPlatformTermsConditions = async () => {
        if (!initialized && !authenticated) {
            return;
        }

        setLoadingAcceptTermsConditions(true);
        setErrorAcceptTermsConditions(null);

        try {
            await userAccountsAPI.post(API_POINTS_PLATFORM + API_USER_TERMS_CONDITIONS);
            setPointsPlatformAccess({
                ...pointsPlatformAccess,
                terms_and_conditions_accepted: true
            });
        } catch (err) {
            setErrorAcceptTermsConditions(err.response);
        } finally {
            setLoadingAcceptTermsConditions(false);
        }
    };

    const publishPointsOffer = (pointsExpirationDate, points, pointPrice, offerValidUntil) => {
        if (initialized && authenticated) {
            setLoadingPointsOffer(true);
            setErrorPointsOffer(null);

            const body = {
                points_expiration_date: pointsExpirationDate,
                points: points,
                points_price: Math.round(pointPrice * 100),
                valid_until: offerValidUntil,
            };

            return customerProductsAPI.post(API_POINTS_PLATFORM_OFFER, body)
                .then(res => {
                    setPointsOffer(res?.data);
                    return res;
                })
                .catch(err => {
                    setErrorPointsOffer(err?.response);
                    throw err.response;
                })
                .finally(() => {
                    setLoadingPointsOffer(false);
                });
        } else {
            return new Promise(resolve => resolve());
        }
    };

    const searchOffer = (points, minimum_expiration_date) => {
        if (initialized && authenticated) {
            setResultOffer(null);
            setLoadingSearchOffer(true);
            setErrorSearchOffer(null);

            return customerProductsAPI.get(API_POINTS_PLATFORM_OFFER_SEARCH + createURLQueryFromParams({points, minimum_expiration_date}))
                .then(res => {
                    setResultOffer(res?.data);
                    return res;
                })
                .catch(err => {
                    setErrorSearchOffer(err?.response);
                    throw err.response;
                })
                .finally(() => {
                    setLoadingSearchOffer(false);
                });
        } else {
            return new Promise(resolve => resolve());
        }
    };

    const purchasePoints = (resultOffer, minimum_expiration_date) => {
        if (initialized && authenticated) {
            setLoadingPointsPurchase(true);
            setErrorPointsPurchase(null);

            const body = {
                offers: resultOffer?.map(o => {
                    return {...o, price_per_point: Math.round(o?.price_per_point * 100)}; // Always transform to currency sub units
                }),
                minimum_expiration_date: minimum_expiration_date
            };

            return customerProductsAPI.post(API_POINTS_PLATFORM_OFFER_PURCHASE, body)
                .then(res => {
                    setPurchasePoints(res?.data);
                    return res;
                })
                .catch(err => {
                    setErrorPointsPurchase(err);
                    throw err.response;
                })
                .finally(() => {
                    setLoadingPointsPurchase(false);
                });
        } else {
            return new Promise(resolve => resolve());
        }
    };

    return {
        deleteSaleOffer,
        deletedSaleOffer,
        loadingDeletedOffer,
        errorDeletedOffer,

        accessAllowed,
        pointsPlatformAccess,
        loadingAccessAllowed,
        errorAccessAllowed,

        acceptPointsPlatformTermsConditions,
        loadingAcceptTermsConditions,
        errorAcceptTermsConditions,

        pointsOffer,
        loadingPointsOffer,
        errorPointsOffer,
        publishPointsOffer,

        resultOffer,
        loadingSearchOffer,
        errorSearchOffer,
        searchOffer,

        purchase,
        purchasePoints,
        loadingPointsPurchase,
        errorPointsPurchase,
    };
};